import {DeleteOutlined, PlusOutlined, QuestionCircleOutlined} from '@ant-design/icons'
import {
  Button,
  Divider,
  Input,
  InputRef,
  Popconfirm,
  Select,
  SelectProps,
  Space,
  Spin,
  Tag,
} from 'antd'
import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {PlantCollection} from '../../../entities/plant'
import {instance as plantsService} from '../../../services/PlantsService'
import {ArgumentOf} from '../../../services/util'
import './plantCollectionSelector.less'

export type PlantCollectionSelectorProps = {
  plantCollections: PlantCollection[]
  plantId: number
  onPlantCollectionsListUpdated: () => void
  userPlantCollections: PlantCollection[]
  className?: string
}

function toSelectItem(collection: PlantCollection) {
  return {
    label: collection.name,
    //value: collection.color,
    value: String(collection.id),
  }
}

type TagRender = SelectProps['tagRender']

const tagRender: TagRender = (props: ArgumentOf<TagRender>) => {
  const {label, value, closable, onClose} = props
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }
  const colors = ['green', 'blue', 'orange', 'cyan']
  return (
    <Tag
      key={value as any}
      color={colors[value % colors.length]}
      //color={value || 'gray'}
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      title={label as string}
      style={{
        marginRight: 3,
        maxWidth: 140,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Tag>
  )
}

export function PlantCollectionSelector({
  plantCollections = [],
  plantId,
  onPlantCollectionsListUpdated = () => {},
  userPlantCollections,
  className,
}: PlantCollectionSelectorProps) {
  const intl = useIntl()

  //const [displayedTags, setDisplayedTags] = useState(plantCollections.map(toSelectItem))
  const displayedTags = plantCollections.map(toSelectItem)
  //const [selectOptions, setSelectOptions] = useState([])
  const [loading, setLoading] = useState(false)

  const inputRef = useRef<InputRef>(null)
  const [name, setName] = useState('')
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  // useEffect(() => {
  //   ;(async () => {
  //     setLoading(true)
  //     try {
  //       const collections = await plantsService.getUserPlantCollections()
  //       setSelectOptions(collections.map(toSelectItem))
  //     } finally {
  //       setLoading(false)
  //     }
  //   })()
  // }, [])

  let index = 0

  async function createCollection(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) {
    e.preventDefault()
    setLoading(true)
    try {
      const result = await plantsService.createPlantCollection(name || `New item ${index++}`)
      if (result.success) {
        onPlantCollectionsListUpdated()
      }
    } finally {
      setName('')
      setLoading(false)
    }
  }

  return (
    <Select
      className={className ? `collection-names ${className}` : 'collection-names'}
      size="large"
      placeholder={intl.formatMessage({id: 'collections'})}
      value={displayedTags as any}
      onChange={async (tags: number[]) => {
        if (loading) {
          return
        }
        setLoading(true)
        const result = await plantsService.changePlantCollections(
          plantId,
          //tags as number[],
          tags.map((collection) => Number(collection)),
          displayedTags.map((collection) => Number(collection.value))
        )
        if (result.success) {
          // setDisplayedTags(
          //   tags.map((tag) => ({
          //     label: selectOptions.find((x) => x.value === tag).label,
          //     value: String(tag),
          //   }))
          // )
          onPlantCollectionsListUpdated()
        }
        setLoading(false)
      }}
      mode="tags"
      options={userPlantCollections.map(toSelectItem)}
      tagRender={tagRender}
      classNames={{
        popup: {
          root: 'collections-dropdown',
        },
      }}
      popupRender={(menu) => (
        <Spin spinning={loading} delay={300}>
          {menu}
          <Divider style={{margin: '8px 0'}} />
          <Space style={{padding: '0 8px 4px'}}>
            <Input
              placeholder={intl.formatMessage({id: 'newCollection'})}
              ref={inputRef}
              value={name}
              onChange={onNameChange}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <Button type="text" icon={<PlusOutlined />} onClick={createCollection}>
              {intl.formatMessage({id: 'add'})}
            </Button>
          </Space>
        </Spin>
      )}
      optionRender={(option) => (
        <div
          className="collection-item"
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              flexGrow: 1,
              flexShrink: 1,
              maxWidth: '100%',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {option.data.label}
          </span>
          <span style={{flexGrow: 0, flexShrink: 0}} onClick={(e) => e.stopPropagation()}>
            <Popconfirm
              placement="bottom"
              title={intl.formatMessage({id: 'areYouSureYouWantToDeleteThisCollection'})}
              description={intl.formatMessage({
                id: 'informationAboutWhichPlantsBelongedToThisCollectionWillAlsoBeRemoved',
              })}
              icon={<QuestionCircleOutlined style={{color: 'red'}} />}
              onConfirm={async () => {
                setLoading(true)
                const result = await plantsService.deletePlantCollection(option.value as number)
                if (result.success) {
                  //setSelectOptions(selectOptions.filter((x) => x.value !== option.value))
                  //setDisplayedTags(displayedTags.filter((x) => x.value !== option.value))
                  onPlantCollectionsListUpdated()
                }
                setLoading(false)
              }}
              okText={intl.formatMessage({id: 'yes'})}
              cancelText={intl.formatMessage({id: 'no'})}
            >
              <DeleteOutlined className="delete-collection" color="red" />
            </Popconfirm>
          </span>
        </div>
      )}
    />
  )
}
